import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePageTitle } from '../../lib/usePageTitle';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { AdminLayout } from './AdminLayout';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { api, ApiError } from '../../lib/api';

interface AdminUserRow {
  id: string;
  email: string;
  displayName: string;
  globalRole: 'SUPER_ADMIN' | 'USER';
  status: string;
  emailVerified: boolean;
  leaguesCount: number;
  createdAt: string;
}

export function AdminUsersPage() {
  usePageTitle('Utenti — Amministrazione');
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['admin', 'users', query],
    queryFn: () =>
      api<{ users: AdminUserRow[] }>(`/admin/users${query ? `?query=${encodeURIComponent(query)}` : ''}`).then(
        (r) => r.users,
      ),
  });

  const updateUser = async (userId: string, body: Record<string, unknown>, message: string) => {
    setError(null);
    const reason = window.prompt('Motivo dell’operazione (obbligatorio, finisce nell’audit log):');
    if (!reason || reason.trim().length < 3) {
      announce('Operazione annullata: serve un motivo.', 'assertive');
      return;
    }
    try {
      await api(`/admin/users/${userId}`, { method: 'PATCH', body: { ...body, reason: reason.trim() } });
      announce(message);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Operazione non riuscita.';
      setError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    }
  };

  return (
    <AdminLayout title="Utenti">
      <form
        noValidate
        className="mt-6 flex max-w-md items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(search.trim());
        }}
      >
        <div className="flex-1">
          <TextField
            label="Cerca utente"
            description="Per email o nome."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button type="submit" variant="secondary">
          Cerca
        </Button>
      </form>

      {error && (
        <p role="alert" className="mt-4 font-medium text-danger">{error}</p>
      )}

      {users.isLoading && (
        <p role="status" className="mt-6">Caricamento utenti…</p>
      )}

      {users.data && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <caption className="visually-hidden">
              Utenti della piattaforma con ruolo, stato e azioni di sospensione
            </caption>
            <thead>
              <tr className="border-b-2 border-ink-300 dark:border-ink-600">
                <th scope="col" className="py-2 pr-3">Utente</th>
                <th scope="col" className="py-2 pr-3">Ruolo</th>
                <th scope="col" className="py-2 pr-3">Stato</th>
                <th scope="col" className="py-2 pr-3 text-right">Leghe</th>
                <th scope="col" className="py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((u) => (
                <tr key={u.id} className="border-b border-ink-200 dark:border-ink-800">
                  <th scope="row" className="py-3 pr-3 font-medium">
                    {u.displayName}
                    <span className="block text-sm font-normal text-ink-600 dark:text-ink-300">
                      {u.email}
                      {!u.emailVerified && ' · email non verificata'}
                    </span>
                  </th>
                  <td className="py-3 pr-3">{u.globalRole === 'SUPER_ADMIN' ? 'Super Admin' : 'Utente'}</td>
                  <td className="py-3 pr-3">
                    {u.status === 'ACTIVE' ? 'Attivo' : u.status === 'SUSPENDED' ? 'Sospeso' : u.status}
                  </td>
                  <td className="py-3 pr-3 text-right">{u.leaguesCount}</td>
                  <td className="py-3">
                    <span className="flex flex-wrap gap-2">
                      {u.status === 'ACTIVE' ? (
                        <Button
                          variant="danger"
                          onClick={() =>
                            updateUser(u.id, { status: 'SUSPENDED' }, `Utente ${u.displayName} sospeso.`)
                          }
                        >
                          Sospendi
                          <span className="visually-hidden"> {u.displayName}</span>
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            updateUser(u.id, { status: 'ACTIVE' }, `Utente ${u.displayName} riattivato.`)
                          }
                        >
                          Riattiva
                          <span className="visually-hidden"> {u.displayName}</span>
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
